import { useState, useEffect, useCallback, useMemo } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const TREASURY_API = `${API_V1}/treasury/fx`;       // existing exposure / revaluation endpoints (S65 + Group 14)
const FX_API = `${API_V1}/fx`;                      // G2.3 monthly + edits + manual-rate endpoints

interface Exposure { currency: string; ar_receivable: number; ap_payable: number; cash: number; net_exposure: number; }
interface ExposureData { exposures: Exposure[]; total_currencies: number; }
interface JELine { account: string; debit: number; credit: number; currency: string; description: string; }
interface RevalResult { revaluation_date: string; total_gain_loss: number; je_lines: JELine[]; rates_used: Record<string, number>; }

// G2.3 monthly grid row -- snake_case matches backend response shape
// (P6 discipline: backend stays canonical, frontend matches names exactly).
interface MonthlyRow {
  from_currency: string;
  to_currency: string;
  closing_rate: number | null;
  average_rate: number | null;
  historical_rate: number | null;
  closing_source: string | null;
  average_source: string | null;
  historical_source: string | null;
  last_edited_at: string | null;
  last_edited_by_id: number | null;
}

interface MonthlyGrid {
  period: string;
  period_end: string;
  rows: MonthlyRow[];
  total_rows: number;
}

interface EditRow {
  id: number;
  from_currency: string;
  to_currency: string;
  rate_date: string;
  rate_type: string;
  action: 'CREATE' | 'UPDATE';
  rate_before: number | null;
  source_before: string | null;
  rate_after: number;
  source_after: string;
  edited_by: number;
  edited_at: string;
  request_origin: string | null;
}

const RATE_TYPES = ['CLOSING', 'AVERAGE', 'HISTORICAL'] as const;
type RateType = typeof RATE_TYPES[number];

// Helper: build period dropdown options (current month + 11 prior + 6 forward).
function buildPeriodOptions(): string[] {
  const opts: string[] = [];
  const now = new Date();
  // 6 forward, 12 backward (covers normal Treasury month-end + Q1 close + adjustments).
  for (let offset = 6; offset >= -12; offset--) {
    const d = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    opts.push(`${y}-${m}`);
  }
  return opts;
}

function currentPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function FXManagement() {
  const { entity } = useGlobalEntity();
  const [exposureData, setExposureData] = useState<ExposureData | null>(null);
  const [revalResult, setRevalResult] = useState<RevalResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [revalLoading, setRevalLoading] = useState(false);
  const [tab, setTab] = useState('exposure');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Rates tab (G2.5 rebuild)
  const periodOptions = useMemo(buildPeriodOptions, []);
  const [period, setPeriod] = useState<string>(currentPeriod());
  const [grid, setGrid] = useState<MonthlyGrid | null>(null);
  const [gridLoading, setGridLoading] = useState(false);
  const [editing, setEditing] = useState<{
    from: string; to: string;
    closing: string; average: string; historical: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  // Add-pair form (for periods where no rows exist yet)
  const [addingPair, setAddingPair] = useState(false);
  const [newPair, setNewPair] = useState({
    from: '', to: '', closing: '', average: '', historical: '',
  });

  // Audit log sub-section
  const [showAudit, setShowAudit] = useState(false);
  const [edits, setEdits] = useState<EditRow[]>([]);
  const [editsLoading, setEditsLoading] = useState(false);

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const expRes = await authFetch(withEntityParam(`${TREASURY_API}/exposure`, entity));
      if (expRes.ok) setExposureData(await expRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  // Load monthly grid when Rates tab opens or period changes.
  const loadGrid = useCallback(async () => {
    setGridLoading(true);
    try {
      const res = await authFetch(`${FX_API}/monthly?period=${period}`);
      if (res.ok) setGrid(await res.json());
      else setGrid(null);
    } catch (e) { console.error(e); setGrid(null); }
    setGridLoading(false);
  }, [period]);

  useEffect(() => { if (tab === 'rates') loadGrid(); }, [tab, loadGrid]);

  // Load audit log on demand.
  const loadEdits = useCallback(async () => {
    setEditsLoading(true);
    try {
      const res = await authFetch(`${FX_API}/edits?limit=50`);
      if (res.ok) {
        const d = await res.json();
        setEdits(d.edits || []);
      }
    } catch (e) { console.error(e); }
    setEditsLoading(false);
  }, []);

  useEffect(() => { if (tab === 'rates' && showAudit) loadEdits(); }, [tab, showAudit, loadEdits]);

  const handleRunRevaluation = async () => {
    setRevalLoading(true);
    try {
      const res = await authFetch(`${TREASURY_API}/revaluation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) { const d = await res.json(); setRevalResult(d); showToast('Revaluation completed', true); }
      else { const e = await res.json(); showToast(e.detail || 'Revaluation failed', false); }
    } catch { showToast('Revaluation failed', false); }
    setRevalLoading(false);
  };

  const handleStartEdit = (row: MonthlyRow) => {
    setEditing({
      from: row.from_currency,
      to: row.to_currency,
      closing: row.closing_rate?.toString() ?? '',
      average: row.average_rate?.toString() ?? '',
      historical: row.historical_rate?.toString() ?? '',
    });
  };

  const handleCancelEdit = () => setEditing(null);

  const handleSaveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    const payload: Record<string, unknown> = {
      from_currency: editing.from,
      to_currency: editing.to,
      period,
    };
    // Null = no change; only send fields the operator filled in OR cleared
    // intentionally. Empty string = no change (preserves existing rate).
    if (editing.closing.trim() !== '') payload.closing_rate = parseFloat(editing.closing);
    if (editing.average.trim() !== '') payload.average_rate = parseFloat(editing.average);
    if (editing.historical.trim() !== '') payload.historical_rate = parseFloat(editing.historical);
    try {
      const res = await authFetch(`${FX_API}/monthly`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        showToast(`Saved ${editing.from}/${editing.to}`, true);
        setEditing(null);
        loadGrid();
        if (showAudit) loadEdits();
      } else {
        const e = await res.json();
        showToast(e.detail || 'Save failed', false);
      }
    } catch { showToast('Save failed', false); }
    setSaving(false);
  };

  const handleAddPair = async () => {
    if (!newPair.from || !newPair.to) {
      showToast('From and To currency required', false);
      return;
    }
    setSaving(true);
    const payload: Record<string, unknown> = {
      from_currency: newPair.from.toUpperCase(),
      to_currency: newPair.to.toUpperCase(),
      period,
    };
    if (newPair.closing.trim() !== '') payload.closing_rate = parseFloat(newPair.closing);
    if (newPair.average.trim() !== '') payload.average_rate = parseFloat(newPair.average);
    if (newPair.historical.trim() !== '') payload.historical_rate = parseFloat(newPair.historical);
    try {
      const res = await authFetch(`${FX_API}/monthly`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        showToast(`Added ${newPair.from}/${newPair.to}`, true);
        setAddingPair(false);
        setNewPair({ from: '', to: '', closing: '', average: '', historical: '' });
        loadGrid();
        if (showAudit) loadEdits();
      } else {
        const e = await res.json();
        showToast(e.detail || 'Add failed', false);
      }
    } catch { showToast('Add failed', false); }
    setSaving(false);
  };

  const totalNetExposure = exposureData ? exposureData.exposures.reduce((sum, e) => sum + e.net_exposure, 0) : 0;
  const largestExposure = exposureData && exposureData.exposures.length > 0
    ? exposureData.exposures.reduce((max, e) => Math.abs(e.net_exposure) > Math.abs(max.net_exposure) ? e : max, exposureData.exposures[0])
    : null;

  const exposureColumns: Column[] = [
    { key: 'currency', label: 'Currency', render: (v: string) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'ar_receivable', label: 'AR Receivable', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'ap_payable', label: 'AP Payable', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'cash', label: 'Cash', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'net_exposure', label: 'Net Exposure', align: 'right', render: (v: number) => <span style={{ fontWeight: 600, color: v < 0 ? 'var(--negative)' : 'var(--text-primary)' }}>{formatAmount(v)}</span> },
  ];

  const jeColumns: Column[] = [
    { key: 'account', label: 'Account', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'debit', label: 'Debit', align: 'right', render: (v: number) => <span style={{ color: v > 0 ? 'var(--positive)' : 'var(--text-secondary)' }}>{v > 0 ? formatAmount(v) : '-'}</span> },
    { key: 'credit', label: 'Credit', align: 'right', render: (v: number) => <span style={{ color: v > 0 ? 'var(--negative)' : 'var(--text-secondary)' }}>{v > 0 ? formatAmount(v) : '-'}</span> },
    { key: 'currency', label: 'Currency' },
    { key: 'description', label: 'Description', render: (v: string) => <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{v}</span> },
  ];

  const renderRatesTab = () => {
    return (
      <div>
        {/* Period selector + add-pair toggle + audit toggle */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Period:</label>
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            style={selectStyle}
          >
            {periodOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <ActionButton label="+ Add Pair" onClick={() => setAddingPair(v => !v)} />
          <ActionButton
            label={showAudit ? 'Hide Edit Log' : 'Show Edit Log'}
            onClick={() => setShowAudit(v => !v)}
          />
          {grid && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8 }}>
              period_end: {grid.period_end} · {grid.total_rows} pair{grid.total_rows === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {/* Add-pair form */}
        {addingPair && (
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 16, marginBottom: 16, border: '1px solid var(--border-default)' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700 }}>Add Currency Pair for {period}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr) auto', gap: 8, alignItems: 'end' }}>
              <div>
                <label style={labelStyle}>From</label>
                <input style={inputStyle} value={newPair.from} maxLength={3}
                  onChange={e => setNewPair({ ...newPair, from: e.target.value.toUpperCase() })} placeholder="SAR" />
              </div>
              <div>
                <label style={labelStyle}>To</label>
                <input style={inputStyle} value={newPair.to} maxLength={3}
                  onChange={e => setNewPair({ ...newPair, to: e.target.value.toUpperCase() })} placeholder="USD" />
              </div>
              <div>
                <label style={labelStyle}>Closing</label>
                <input type="number" step="any" style={inputStyle} value={newPair.closing}
                  onChange={e => setNewPair({ ...newPair, closing: e.target.value })} placeholder="0.2667" />
              </div>
              <div>
                <label style={labelStyle}>Average</label>
                <input type="number" step="any" style={inputStyle} value={newPair.average}
                  onChange={e => setNewPair({ ...newPair, average: e.target.value })} placeholder="0.2670" />
              </div>
              <div>
                <label style={labelStyle}>Historical</label>
                <input type="number" step="any" style={inputStyle} value={newPair.historical}
                  onChange={e => setNewPair({ ...newPair, historical: e.target.value })} placeholder="0.2660" />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <ActionButton label={saving ? 'Saving…' : 'Add'} onClick={handleAddPair} disabled={saving} />
                <ActionButton label="Cancel" onClick={() => { setAddingPair(false); setNewPair({ from: '', to: '', closing: '', average: '', historical: '' }); }} />
              </div>
            </div>
          </div>
        )}

        {/* Per-month grid */}
        {gridLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>
        ) : grid && grid.rows.length === 0 ? (
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
            No FX rates published for {period}. Use "+ Add Pair" to publish.
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                {['From', 'To', 'Closing', 'Average', 'Historical', 'Last Edited', ''].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid?.rows.map((row, idx) => {
                const isEditing = editing && editing.from === row.from_currency && editing.to === row.to_currency;
                return (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border-default)' }}>
                    <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--accent)' }}>{row.from_currency}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{row.to_currency}</td>
                    <td style={tdStyle}>
                      {isEditing ? (
                        <input type="number" step="any" style={inlineInputStyle} value={editing!.closing}
                          onChange={e => setEditing({ ...editing!, closing: e.target.value })} />
                      ) : renderRateCell(row.closing_rate, row.closing_source)}
                    </td>
                    <td style={tdStyle}>
                      {isEditing ? (
                        <input type="number" step="any" style={inlineInputStyle} value={editing!.average}
                          onChange={e => setEditing({ ...editing!, average: e.target.value })} />
                      ) : renderRateCell(row.average_rate, row.average_source)}
                    </td>
                    <td style={tdStyle}>
                      {isEditing ? (
                        <input type="number" step="any" style={inlineInputStyle} value={editing!.historical}
                          onChange={e => setEditing({ ...editing!, historical: e.target.value })} />
                      ) : renderRateCell(row.historical_rate, row.historical_source)}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 11, color: 'var(--text-secondary)' }}>
                      {row.last_edited_at ? (
                        <>
                          {new Date(row.last_edited_at).toLocaleString()}
                          {row.last_edited_by_id ? <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>(user #{row.last_edited_by_id})</span> : null}
                        </>
                      ) : '—'}
                    </td>
                    <td style={tdStyle}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <ActionButton label={saving ? '…' : 'Save'} onClick={handleSaveEdit} disabled={saving} />
                          <ActionButton label="Cancel" onClick={handleCancelEdit} />
                        </div>
                      ) : (
                        <ActionButton label="Edit" onClick={() => handleStartEdit(row)} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Audit log sub-section */}
        {showAudit && (
          <div style={{ marginTop: 24, background: 'var(--bg-card)', borderRadius: 8, padding: 16 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700 }}>Recent Edits (last 50)</h4>
            {editsLoading ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>
            ) : edits.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>No edits yet.</div>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                    {['When', 'User', 'Pair', 'Type', 'Date', 'Action', 'Before → After', 'Origin'].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {edits.map(e => (
                    <tr key={e.id} style={{ borderBottom: '1px solid var(--border-default)' }}>
                      <td style={{ ...tdStyle, fontSize: 11 }}>{new Date(e.edited_at).toLocaleString()}</td>
                      <td style={tdStyle}>#{e.edited_by}</td>
                      <td style={tdStyle}>{e.from_currency}/{e.to_currency}</td>
                      <td style={tdStyle}>{e.rate_type}</td>
                      <td style={tdStyle}>{e.rate_date}</td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: e.action === 'CREATE' ? 'var(--positive-bg)' : 'var(--accent-bg)' }}>
                          {e.action}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>
                        {e.rate_before !== null ? e.rate_before.toFixed(6) : '—'}
                        {' → '}
                        {e.rate_after.toFixed(6)}
                      </td>
                      <td style={{ ...tdStyle, fontSize: 11, color: 'var(--text-secondary)' }}>{e.request_origin || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <PageLayout title="FX Management" subtitle="Foreign exchange exposure, revaluation, and rates">
      {exposureData && (
        <KpiGrid
          columns={3}
          items={[
            { label: 'Total Currencies', value: exposureData.total_currencies, color: 'var(--accent)' },
            { label: 'Largest Exposure', value: largestExposure ? `${largestExposure.currency} ${formatAmountCompact(largestExposure.net_exposure)}` : '-' },
            { label: 'Total Net Exposure', value: formatAmountCompact(totalNetExposure), color: totalNetExposure < 0 ? 'var(--negative)' : 'var(--positive)' },
          ]}
        />
      )}

      <TabBar
        tabs={[
          { key: 'exposure', label: 'Exposure' },
          { key: 'revaluation', label: 'Revaluation' },
          { key: 'rates', label: 'Rates' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'exposure' ? (
        <DataTable
          columns={exposureColumns}
          rows={exposureData?.exposures || []}
          loading={loading}
          emptyMessage="No FX exposure data available."
        />
      ) : tab === 'revaluation' ? (
        <div>
          <div style={{ marginBottom: 16 }}>
            <ActionButton label={revalLoading ? 'Running...' : 'Run Revaluation'} onClick={handleRunRevaluation} disabled={revalLoading} />
          </div>
          {revalResult ? (
            <div>
              <KpiGrid
                columns={2}
                items={[
                  { label: 'Revaluation Date', value: revalResult.revaluation_date },
                  { label: 'Total Gain / Loss', value: formatAmountCompact(revalResult.total_gain_loss), color: revalResult.total_gain_loss < 0 ? 'var(--negative)' : 'var(--positive)' },
                ]}
              />
              {revalResult.je_lines.length > 0 && (
                <DataTable columns={jeColumns} rows={revalResult.je_lines} />
              )}
            </div>
          ) : <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Click "Run Revaluation" to generate FX revaluation entries.</div>}
        </div>
      ) : renderRatesTab()}

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}

// ─── Cell + form styles ─────────────────────────────────────────

function renderRateCell(rate: number | null, source: string | null) {
  if (rate === null) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  return (
    <span>
      <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{rate.toFixed(6)}</span>
      {source ? <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--text-secondary)', padding: '1px 6px', borderRadius: 3, background: 'var(--bg-subtle)' }}>{source}</span> : null}
    </span>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 2, fontWeight: 600,
};

const inputStyle: React.CSSProperties = {
  width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)',
  border: '1px solid var(--border-default)',
  borderRadius: 4, padding: '6px 10px', fontSize: 13, boxSizing: 'border-box',
};

const inlineInputStyle: React.CSSProperties = {
  ...inputStyle, padding: '4px 6px', fontSize: 12, width: 110,
};

const selectStyle: React.CSSProperties = {
  background: 'var(--bg-card)', color: 'var(--text-primary)',
  border: '1px solid var(--border-default)',
  borderRadius: 4, padding: '6px 10px', fontSize: 13,
};

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse',
};

const thStyle: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'left', color: 'var(--text-secondary)',
  fontSize: 12, fontWeight: 600,
};

const tdStyle: React.CSSProperties = {
  padding: '8px 10px', color: 'var(--text-primary)', fontSize: 13,
};
